import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Users,
  Link,
  Unlink,
  Search,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { listPlayerConstructs, listUsers } from "../../graphql/queries";
import { updatePlayerConstruct } from "../../graphql/mutations";
import type { PlayerConstruct, User } from "../../lib/types";

const client = generateClient();

const PlayerConstructMapping: React.FC = () => {
  const [playerConstructs, setPlayerConstructs] = useState<PlayerConstruct[]>(
    []
  );
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "mapped" | "unmapped"
  >("all");
  const [isLoading, setIsLoading] = useState(true);
  const [mappingStatus, setMappingStatus] = useState<string>("");

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load player constructs
      const constructsResult = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });

      // Load users
      const usersResult = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });

      setPlayerConstructs(
        constructsResult.data.listPlayerConstructs.items as PlayerConstruct[]
      );
      setUsers(usersResult.data.listUsers.items as User[]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter constructs
  const filteredConstructs = playerConstructs.filter((construct) => {
    const matchesSearch = construct.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "mapped" && construct.userId) ||
      (filterStatus === "unmapped" && !construct.userId);

    return matchesSearch && matchesFilter;
  });

  // Map construct to user
  const mapConstructToUser = async (
    constructId: string,
    userId: string | null
  ) => {
    setMappingStatus("Mapping...");
    try {
      await client.graphql({
        query: updatePlayerConstruct,
        variables: {
          input: {
            id: constructId,
            userId: userId,
          },
        },
      });

      // Update local state
      setPlayerConstructs((prev) =>
        prev.map((construct) =>
          construct.id === constructId
            ? { ...construct, userId: userId as string | null }
            : construct
        )
      );

      setMappingStatus("Mapping updated successfully!");
      setTimeout(() => setMappingStatus(""), 3000);
    } catch (error) {
      console.error("Error mapping construct:", error);
      setMappingStatus("Error updating mapping. Please try again.");
    }
  };

  // Get user display name
  const getUserDisplayName = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.displayName : "Unknown User";
  };

  // Get user email
  const getUserEmail = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.email : "";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Player Construct Mapping</h1>
        <p className="text-muted-foreground">
          Map imported player names to user accounts. Multiple names mapped to
          one account are combined in league standings.
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{playerConstructs.length}</p>
                <p className="text-sm text-muted-foreground">
                  Total Constructs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Link className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">
                  {playerConstructs.filter((c) => c.userId).length}
                </p>
                <p className="text-sm text-muted-foreground">Mapped</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Unlink className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-2xl font-bold">
                  {playerConstructs.filter((c) => !c.userId).length}
                </p>
                <p className="text-sm text-muted-foreground">Unmapped</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search player constructs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={filterStatus}
              onValueChange={(value: any) => setFilterStatus(value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Constructs</SelectItem>
                <SelectItem value="mapped">Mapped Only</SelectItem>
                <SelectItem value="unmapped">Unmapped Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Mapping Status */}
      {mappingStatus && (
        <Card
          className={
            mappingStatus.includes("Error")
              ? "border-red-200 bg-red-50"
              : "border-green-200 bg-green-50"
          }
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              {mappingStatus.includes("Error") ? (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
              <p className="text-sm">{mappingStatus}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Player Constructs List */}
      <Card>
        <CardHeader>
          <CardTitle>Player Constructs</CardTitle>
          <CardDescription>
            {filteredConstructs.length} of {playerConstructs.length} constructs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredConstructs.map((construct) => (
              <div
                key={construct.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{construct.name}</h3>
                    {construct.userId ? (
                      <Badge
                        variant="default"
                        className="bg-green-100 text-green-800"
                      >
                        <Link className="h-3 w-3 mr-1" />
                        Mapped
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <Unlink className="h-3 w-3 mr-1" />
                        Unmapped
                      </Badge>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-muted-foreground">
                    <span className="mr-4">
                      Events: {construct.totalEvents}
                    </span>
                    <span className="mr-4">
                      Points: {construct.totalPoints}
                    </span>
                    <span className="mr-4">
                      Record: {construct.totalWins}-{construct.totalLosses}-
                      {construct.totalDraws}
                    </span>
                    {construct.bestRank && (
                      <span>Best Rank: {construct.bestRank}</span>
                    )}
                  </div>

                  {construct.userId && (
                    <div className="mt-2 text-sm">
                      <span className="text-green-600 font-medium">
                        Mapped to: {getUserDisplayName(construct.userId)}
                      </span>
                      <span className="text-muted-foreground ml-2">
                        ({getUserEmail(construct.userId)})
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {construct.userId ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => mapConstructToUser(construct.id, null)}
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Unmap
                    </Button>
                  ) : (
                    <Select
                      onValueChange={(userId) =>
                        mapConstructToUser(construct.id, userId)
                      }
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Map to user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.displayName} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            ))}

            {filteredConstructs.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No player constructs found matching your criteria</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlayerConstructMapping;
